import React, { useEffect, useId, useState } from 'react';
import Dialog from '../../common/Dialog';
import { PatientDocumentsService } from '../../../services/patientmr/patientDocuments.service';
import { validateUploadFile } from '../../../services/uploads/presignedUpload';
import {
  PACKET_DOCUMENT_TYPES,
  PACKET_SERVICES,
  PATIENT_DOCUMENT_TYPES,
  PATIENT_DOCUMENT_TYPE_LABELS,
  isPacketDocumentType,
} from '../../../dtos/patientmr/patientDocuments.dto';
import type { PacketService, PatientDocument, PatientDocumentType } from '../../../dtos/patientmr/patientDocuments.dto';

export interface UploadDocumentDialogProps {
  open: boolean;
  patientId: string;
  defaultType?: PatientDocumentType;
  /** Packet scans: the service an informed consent covers */
  defaultService?: PacketService | null;
  /** Prefilled title (e.g. the packet template title) */
  defaultTitle?: string;
  onClose: () => void;
  onUploaded: (doc: PatientDocument) => void;
}

const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Upload dialog for the patient documents library: type, date, title and file (pre-signed upload).
 * Phase 19 F18: an admission packet type is a wet-signed scan, so it also asks who signed it
 * (and the service, for a consent to services); the date is the signing date.
 */
const UploadDocumentDialog: React.FC<UploadDocumentDialogProps> = ({ open, patientId, defaultType = 'OTHER', defaultService = null, defaultTitle = '', onClose, onUploaded }) => {
  const baseId = useId();
  const [type, setType] = useState<PatientDocumentType>(defaultType);
  const [documentDate, setDocumentDate] = useState(todayIso());
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [signerName, setSignerName] = useState('');
  const [signerRelationship, setSignerRelationship] = useState('');
  const [service, setService] = useState<PacketService | ''>(defaultService ?? '');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setType(defaultType);
    setDocumentDate(todayIso());
    setTitle(defaultTitle);
    setFile(null);
    setSignerName('');
    setSignerRelationship('');
    setService(defaultService ?? '');
    setProgress(null);
    setError(null);
  }, [open, defaultType, defaultService, defaultTitle]);

  const isPacket = isPacketDocumentType(type);
  const needsService = type === 'INFORMED_CONSENT';

  const uploading = progress !== null;

  const chooseFile = (picked: File | undefined) => {
    setError(null);
    if (!picked) {
      setFile(null);
      return;
    }
    const invalid = validateUploadFile(picked);
    if (invalid) {
      setFile(null);
      setError(invalid);
      return;
    }
    setFile(picked);
    if (!title.trim()) setTitle(picked.name.replace(/\.[^.]+$/, ''));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Choose a file to upload.');
      return;
    }
    if (!title.trim() || !documentDate) {
      setError('Title and document date are required.');
      return;
    }
    if (isPacket && !signerName.trim()) {
      setError('Enter the name of the person who signed the form.');
      return;
    }
    if (needsService && !service) {
      setError('Choose the service this consent covers.');
      return;
    }
    setError(null);
    setProgress(0);
    try {
      const doc = await PatientDocumentsService.upload(
        patientId,
        file,
        {
          type,
          documentDate,
          title: title.trim(),
          ...(isPacket ? { signerName: signerName.trim(), signerRelationship: signerRelationship.trim() } : {}),
          ...(needsService && service ? { service } : {}),
        },
        (pct) => setProgress(pct)
      );
      setProgress(null);
      onUploaded(doc);
    } catch (err) {
      setProgress(null);
      setError(err instanceof Error && err.message ? err.message : 'The upload failed. Try again.');
    }
  };

  const input = 'mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm';

  return (
    <Dialog open={open} onClose={() => { if (!uploading) onClose(); }} labelledBy={`${baseId}-title`}>
      <form onSubmit={handleSubmit} className="space-y-3 text-left" noValidate>
        <h3 id={`${baseId}-title`} className="text-lg font-semibold text-slate-800 pr-6">Upload document</h3>
        <label className="block text-sm font-medium text-slate-700">
          Type
          <select aria-label="Document type" value={type} onChange={(e) => setType(e.target.value as PatientDocumentType)} className={input} disabled={uploading}>
            {PATIENT_DOCUMENT_TYPES.map((t) => (
              <option key={t} value={t}>{PATIENT_DOCUMENT_TYPE_LABELS[t]}</option>
            ))}
            <optgroup label="Admission packet (signed scan)">
              {PACKET_DOCUMENT_TYPES.map((t) => (
                <option key={t} value={t}>{PATIENT_DOCUMENT_TYPE_LABELS[t]}</option>
              ))}
            </optgroup>
          </select>
        </label>
        {isPacket && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-sm font-medium text-slate-700">
              Signed by
              <input aria-label="Signed by" type="text" value={signerName} maxLength={120} onChange={(e) => setSignerName(e.target.value)} className={input} disabled={uploading} required />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Relationship to the patient
              <input aria-label="Relationship to the patient" type="text" value={signerRelationship} maxLength={60} onChange={(e) => setSignerRelationship(e.target.value)} className={input} disabled={uploading} placeholder="e.g. Mother, Self" />
            </label>
          </div>
        )}
        {needsService && (
          <label className="block text-sm font-medium text-slate-700">
            Service
            <select aria-label="Service" value={service} onChange={(e) => setService(e.target.value as PacketService | '')} className={input} disabled={uploading}>
              <option value="">Choose a service</option>
              {PACKET_SERVICES.map((sv) => <option key={sv} value={sv}>{sv}</option>)}
            </select>
          </label>
        )}
        <label className="block text-sm font-medium text-slate-700">
          {isPacket ? 'Signing date' : 'Document date'}
          <input aria-label="Document date" type="date" value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} className={input} disabled={uploading} required />
        </label>
        <label className="block text-sm font-medium text-slate-700">
          Title
          <input aria-label="Title" type="text" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} className={input} disabled={uploading} required />
        </label>
        <label className="block text-sm font-medium text-slate-700">
          File
          <input
            aria-label="File"
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            onChange={(e) => chooseFile(e.target.files?.[0])}
            className="mt-1 block w-full text-sm"
            disabled={uploading}
          />
          <span className="mt-1 block text-xs font-normal text-slate-500">PDF, JPG or PNG, up to 10 MB.</span>
        </label>
        {type === 'PHYSICIAN_ORDER' && (
          <p className="text-xs text-amber-800">Physicians must sign; stamps are not accepted.</p>
        )}
        {uploading && (
          <div className="h-2 w-full overflow-hidden rounded bg-slate-200" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress ?? 0}>
            <div className="h-2 bg-slate-700 transition-all" style={{ width: `${progress ?? 0}%` }} />
          </div>
        )}
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} disabled={uploading} className="min-h-[44px] rounded-md border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            Cancel
          </button>
          <button type="submit" disabled={uploading || !file} className="min-h-[44px] rounded-md bg-slate-800 px-4 py-2 text-sm text-white hover:bg-slate-700 disabled:opacity-50">
            {uploading ? `Uploading ${progress ?? 0}%` : 'Upload'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default UploadDocumentDialog;
